import { useEffect, useState } from 'react';

/**
 * What the browser reports about itself, resolved after mount and then watched.
 *
 * Capability that can only be known by trying — whether a WebGL context will
 * actually be granted — is not guessed here; `SceneBoundary` catches that for
 * real. Every downgrade is announced with `console.info` at the point of use,
 * because a silent fallback survives every screenshot.
 */
export interface DeviceProfile {
  reducedMotion: boolean;
  coarsePointer: boolean;
  /** Narrow enough that the scene budget has to come down. */
  compact: boolean;
  /** Whether this browser will grant a WebGL context at all. */
  webglAvailable: boolean;
  /** False until the browser has actually been measured. */
  measured: boolean;
}

/**
 * Probed exactly once per document, then cached.
 *
 * Skipping this check entirely was worse than a flaky check: a browser with no
 * WebGL downloaded the whole scene chunk, let three.js fail, and logged three
 * console errors before the boundary caught it. Probing repeatedly was worse
 * still — every probe holds a real context and browsers cap how many exist, so
 * this runs a single time and hands its context straight back.
 */
let webglSupport: boolean | undefined;

function detectWebgl(): boolean {
  if (webglSupport !== undefined) {
    return webglSupport;
  }

  if (typeof document === 'undefined') {
    return false;
  }

  try {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('webgl2') ?? canvas.getContext('webgl');

    context?.getExtension('WEBGL_lose_context')?.loseContext();
    webglSupport = Boolean(context);
  } catch {
    webglSupport = false;
  }

  return webglSupport;
}

const queries = {
  reducedMotion: '(prefers-reduced-motion: reduce)',
  coarsePointer: '(pointer: coarse)',
  compact: '(max-width: 767px)',
} as const;

/**
 * First render always uses these, on the server and in the browser alike.
 *
 * The page is prerendered, so a client that measured the real device on its
 * first pass would disagree with the shipped markup and fail hydration. The
 * measured values arrive in an effect, one frame later.
 */
const conservative: DeviceProfile = {
  reducedMotion: false,
  coarsePointer: false,
  compact: false,
  webglAvailable: false,
  measured: false,
};

function read(): DeviceProfile {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return conservative;
  }

  return {
    reducedMotion: window.matchMedia(queries.reducedMotion).matches,
    coarsePointer: window.matchMedia(queries.coarsePointer).matches,
    compact: window.matchMedia(queries.compact).matches,
    webglAvailable: detectWebgl(),
    measured: true,
  };
}

export function useDeviceProfile(): DeviceProfile {
  const [profile, setProfile] = useState<DeviceProfile>(conservative);

  useEffect(() => {
    const lists = Object.values(queries).map((query) => window.matchMedia(query));
    const onChange = () => setProfile(read());

    lists.forEach((list) => list.addEventListener('change', onChange));
    onChange();

    return () => lists.forEach((list) => list.removeEventListener('change', onChange));
  }, []);

  return profile;
}
